import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";

function normalizePaymentCard(card) {
  const membershipCreditCardId =
    card?.membershipCreditCardId ??
    card?.functionCreditCardId ??
    card?.token ??
    null;
  const functionCreditCardId =
    card?.functionCreditCardId ??
    card?.membershipCreditCardId ??
    card?.token ??
    null;

  return {
    id:
      card?.membershipCreditCardId ??
      card?.functionCreditCardId ??
      card?.token ??
      `${card?.lastFour ?? "card"}`,
    cardId: membershipCreditCardId,
    membershipCreditCardId,
    functionCreditCardId,
    nickName: card?.nickName ?? card?.nickname ?? "Card",
    lastFour: String(card?.lastFour ?? ""),
    expireMonth: String(card?.expireMonth ?? card?.expMonth ?? ""),
    expireYear: String(card?.expireYear ?? card?.expYear ?? ""),
    zipCode: String(card?.zip ?? ""),
    cardType: card?.cardType ?? "Card",
    isPrimary: Boolean(card?.isPrimary),
    isSelected: Boolean(card?.isPrimary),
    isChecked: Boolean(card?.isPrimary),
    raw: card,
  };
}

function normalizePaymentMethodsResponse(payload) {
  const cards = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.cards)
    ? payload.cards
    : [];

  return cards.map(normalizePaymentCard);
}

function pickDefined(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== undefined && value !== null
    )
  );
}

function nonEmpty(value) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeExpireMonth(value) {
  const raw = nonEmpty(String(value ?? ""));
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, "");
  if (!digits) return undefined;
  return digits.padStart(2, "0").slice(-2);
}

function normalizeExpireYear(value) {
  const raw = nonEmpty(String(value ?? ""));
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, "");
  if (!digits) return undefined;
  return digits.slice(-2);
}

function toUpdatePaymentPayload(input = {}) {
  const raw = input?.raw ?? {};
  const expMonth =
    nonEmpty(input?.expireMonth) ??
    nonEmpty(input?.expMonth) ??
    nonEmpty(raw?.expireMonth) ??
    nonEmpty(raw?.expMonth);
  const expYear =
    nonEmpty(input?.expireYear) ??
    nonEmpty(input?.expYear) ??
    nonEmpty(raw?.expireYear) ??
    nonEmpty(raw?.expYear);
  const membershipCreditCardId =
    input?.membershipCreditCardId ??
    raw?.membershipCreditCardId ??
    input?.cardId ??
    raw?.functionCreditCardId;
  const derivedCardHolder =
    `${raw?.first_name ?? ""} ${raw?.last_name ?? ""}`.trim() || undefined;
  const cardHolder =
    nonEmpty(input?.cardHolder) ??
    nonEmpty(raw?.cardHolder) ??
    derivedCardHolder;

  return pickDefined({
    expireMonth: normalizeExpireMonth(expMonth),
    expireYear: normalizeExpireYear(expYear),
    cardHolder,
    nickname:
      nonEmpty(input?.nickname) ??
      nonEmpty(input?.nickName) ??
      nonEmpty(raw?.nickname) ??
      nonEmpty(raw?.nickName),
    zip:
      nonEmpty(input?.zip) ??
      nonEmpty(input?.zipCode) ??
      nonEmpty(raw?.zip) ??
      nonEmpty(raw?.postalCode),
    membershipCreditCardId,
    isPrimary: Boolean(input?.isPrimary ?? raw?.isPrimary),
  });
}

export function usePaymentMethods(options = {}) {
  const { enabled = true } = options;
  return useQuery(
    "paymentMethods",
    async () => {
      const res = await makeAuthedRequest("/payment-methods");
      if (!res.ok)
        throw await parseApiError(res, "Payment methods fetch failed");
      const payload = await res.json();
      return normalizePaymentMethodsResponse(payload);
    },
    { enabled }
  );
}

export function useAddPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload) => {
      const res = await makeAuthedRequest("/payment-methods", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await parseApiError(res, "Add payment method failed");
      return res.json();
    },
    {
      onSuccess: () => queryClient.invalidateQueries("paymentMethods"),
      // CardPaymentModel renders this failure inline on the card form.
      meta: { suppressErrorModal: true },
    }
  );
}

export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ cardId, ...payload }) => {
      const updatePayload = toUpdatePaymentPayload(payload);
      const res = await makeAuthedRequest(`/payment-methods/${cardId}`, {
        method: "PUT",
        body: JSON.stringify(updatePayload),
      });
      if (!res.ok) {
        throw await parseApiError(res, "Update payment method failed");
      }

      // Some successful update responses return no body.
      const rawBody = await res.text();
      if (!rawBody) return null;

      try {
        return JSON.parse(rawBody);
      } catch {
        return rawBody;
      }
    },
    {
      onSuccess: () => queryClient.invalidateQueries("paymentMethods"),
      // SavedCardsModal renders this failure inline next to the card it edits.
      meta: { suppressErrorModal: true },
    }
  );
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation(
    async (cardId) => {
      const res = await makeAuthedRequest(`/payment-methods/${cardId}`, {
        method: "DELETE",
      });
      if (!res.ok)
        throw await parseApiError(res, "Delete payment method failed");
    },
    {
      onSuccess: () => queryClient.invalidateQueries("paymentMethods"),
      // SavedCardsModal renders this failure inline (e.g. deleting the primary card).
      meta: { suppressErrorModal: true },
    }
  );
}
